import React, { useEffect, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { IoIosArrowRoundBack, IoMdEye } from "react-icons/io";
import { MdFilterAlt } from "react-icons/md";
import { RiDeleteBin6Line, RiHotelLine } from "react-icons/ri";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Pagination from "../../../../components/common/Pagination";
import { FiEdit, FiTrash } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  confirmTableReservation,
  cancelBooking,
} from "../../../../api/owner/restaurants/reservations";
import {
  getAllBookings,
  getBookingDetail,
  deleteBooking,
  updateBookingStatus,
} from "../../../../api/owner/hotels/bookings";
import socket from "../../../../helper/socket";
import Swal from "sweetalert2";
import { useTranslation } from "../../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[978px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const ForRestaurants = () => {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const statuses = [
    "Booked",
    "Checked-in",
    "Checked-out",
    "Available",
    "Completed",
    "Cancelled",
    "Pending",
  ];
  
  // Map status values to translation keys
  const getStatusTranslation = (statusValue) => {
    if (!statusValue) return t("hotelbooking").statuses.all;
    const statusMap = {
      "Booked": t("hotelbooking").statuses.booked,
      "Checked-in": t("hotelbooking").statuses.checkedIn,
      "Checked-out": t("hotelbooking").statuses.checkedOut,
      "Available": t("hotelbooking").statuses.available,
      "Completed": t("hotelbooking").statuses.completed,
      "Cancelled": t("hotelbooking").statuses.cancelled,
      "Pending": t("hotelbooking").statuses.pending,
    };
    return statusMap[statusValue] || statusValue;
  };
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 10;
  const navigate = useNavigate();
  const [selectedBookingId, setselectedBookingId] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    tableNo: "",
    discountPercentage: "",
    tokenCharges: "",
    bookingStatus: "pending",
    paymentStatus: "pending",
  });

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["bookings", page, limit, status, search],
    queryFn: () => getAllBookings({ page, limit, status, search }),
    keepPreviousData: true,
  });

  const { data: OrdersDetailData, isLoading: isOrderDetailLoading } = useQuery({
    queryKey: ["rbookings-detail", selectedBookingId],
    queryFn: () => getBookingDetail(selectedBookingId),
    enabled: !!selectedBookingId && isPopupOpen,
    keepPreviousData: true,
  });

  const orders = data?.data?.bookings || [];
  const pagination = data?.data?.pagination || {};
  const orderDetail = OrdersDetailData?.data?.booking;

  const updateMutation = useMutation({
    mutationFn: (data) => updateBookingStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["bookings"]);
      queryClient.invalidateQueries(["rbookings-detail"]);
      toast.success("Booking status updated");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || t("hotelbooking").errors.updateStatus;
      toast.error(errorMessage);
    },
  });

  const cancelBookingMutation = useMutation({
    mutationFn: (data) => cancelBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["bookings-detail"]);
      toast.success(t("hotelbooking").bookingCanceled);
      setIsPopupOpen(false);
      Swal.close();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("hotelbooking").errors.cancelBooking;
      toast.error(errorMessage);
    },
  });

  const deleteBookingMutation = useMutation({
    mutationFn: (data) => deleteBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["bookings"]);
      toast.success(t("hotelbooking").success.deleted);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("hotelbooking").errors.deleteBooking;
      toast.error(errorMessage);
    },
  });

  useEffect(() => {
    const handleNewBooking = () => {
      refetch();
    };

    socket.on("hotelBooking", handleNewBooking);

    return () => {
      socket.off("hotelBooking", handleNewBooking);
    };
  }, [refetch]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500); // 500ms delay

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // const handleFilterChange = (value) => {
  //   setStatus(value);
  //   setIsOpen(false);
  // };

  const handleFilterChange = (selectedStatus) => {
    setStatus(selectedStatus);
    setIsOpen(false);
    setPage(1);
    refetch();
  };

  const openModel = (id, status) => {
    setselectedBookingId(id);
    setFormData((prev) => ({
      ...prev,
      bookingStatus: status,

    }));

    setIsPopupOpen(true);
  };

  const validateForm = (formData, enabled) => {
    const newErrors = {};
    if (formData.discountPercentage === "") {
      newErrors.discountPercentage =
        t("hotelbooking").errors.discountPercentageRequired;
    } else if (isNaN(formData.discountPercentage)) {
      newErrors.discountPercentage =
        t("hotelbooking").errors.discountPercentageNumber;
    } else if (
      formData.discountPercentage < 0 ||
      formData.discountPercentage > 100
    ) {
      newErrors.discountPercentage =
        t("hotelbooking").errors.discountPercentageRange;
    }

    if (formData.tableNo === "") {
      newErrors.tableNo = t("hotelbooking").errors.tableNumberRequired;
    } else if (isNaN(formData.tableNo)) {
      newErrors.tableNo = t("hotelbooking").errors.tableNumberNumber;
    }

    if (enabled) {
      if (formData.tokenCharges === "") {
        newErrors.tokenCharges = t("hotelbooking").errors.tokenChargesRequired;
      } else if (isNaN(formData.tokenCharges)) {
        newErrors.tokenCharges = t("hotelbooking").errors.tokenChargesNumber;
      } else if (formData.tokenCharges < 0) {
        newErrors.tokenCharges = t("hotelbooking").errors.tokenChargesNegative;
      }
    }

    return newErrors;
  };

  const handleUpdateStatus = () => {
    updateMutation.mutate({
      bookingId: selectedBookingId,
      status: formData.bookingStatus,
      paymentStatus: formData.paymentStatus,
    });
    // Swal.fire({
    //   title: t("hotelbooking").confirmUpdateStatus,
    //   icon: "warning",
    //   showCancelButton: true,
    //   confirmButtonText: "Yes",
    //   cancelButtonText: "No",
    // }).then((result) => {
    //   if (result.isConfirmed) {
    //     updateMutation.mutate({
    //       bookingId: selectedBookingId,
    //       status: formData.bookingStatus,
    //       paymentStatus: formData.paymentStatus,
    //     });
    //   }
    // });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t("forRestaurants").confirmDialog.deleteTitle,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("forRestaurants").confirmDialog.deleteConfirmBtn,
      cancelButtonText: t("forRestaurants").confirmDialog.deleteDenyBtn,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteBookingMutation.mutate(id);
      }
    });
  };

  return (
    <>
      <div className="flex justify-between items-center flex-wrap gap-[20px]">
        <div className="flex gap-[10px] items-center">
          <div className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]">
            <IoIosArrowRoundBack
              onClick={() => navigate(-1)}
              size={24}
              className="text-[#fff]"
            />
          </div>
          <h2 className="text-[22px] font-medium">
            {t("hotelbooking").hotelReservations}
          </h2>
        </div>
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="py-[9px] px-[35px] bg-white text-base  font-medium flex  items-center gap-[6px] rounded-[2px] border border-[#DDDDDD]"
          >
            <MdFilterAlt /> {status ? getStatusTranslation(status) : t("hotelbooking").filter}
          </button>
          {isOpen && (
            <div className="absolute z-10 mt-2 w-36 bg-white border rounded shadow text-sm font-medium">
              {/* All statuses option */}
              <div
                onClick={() => handleFilterChange("")}
                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                  status === "" ? "bg-gray-200 font-semibold" : ""
                }`}
              >
                {t("hotelbooking").statuses.all}
              </div>
              {statuses.map((item) => (
                <div
                  key={item}
                  onClick={() => handleFilterChange(item)}
                  className={`px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer ${
                    status === item ? "bg-gray-200 font-semibold" : ""
                  }`}
                >
                  {getStatusTranslation(item)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mt-[31px]">
        <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden mt-[28px] py-[20px] sm:px-[30px] px-[15px] bg-white grid">
          <div className="flex justify-between items-center py-[13px] flex-wrap  gap-[10px]">
            <h2 className="text-base font-semibold">
              {t("hotelbooking").reservationList}
            </h2>
            <div className="relative w-full max-w-[301px]">
              <span className="absolute inset-y-0 left-3 flex items-center">
                <CiSearch size={24} className="text-[#4CAF50]" />
              </span>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t("hotelbooking").search}
                className="w-full pl-10 pr-4 py-2 rounded-full border border-[#DDDDDD] focus:outline-none focus:border-[#4CAF50]"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : isError ? (
            <p className="text-red-500 text-center mt-6">{error.message}</p>
          ) : (
            <div className="overflow-x-auto md:mt-[30px] mt-[20px]">
              <table className="min-w-full  ">
                <thead className="bg-[#FFFAF2] ">
                  <tr className="">
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("hotelbooking").bookingId}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("hotelbooking").hotelName}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("hotelbooking").guestName}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("hotelbooking").roomNumber}
                    </th>

                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("hotelbooking").checkInDate}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] max-w-[197px] whitespace-nowrap">
                      {t("hotelbooking").checkOutDate}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("hotelbooking").guests}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("hotelbooking").activities}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("hotelbooking").bookingStatus}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] max-w-[168px] whitespace-nowrap">
                      {t("hotelbooking").payment}
                    </th>

                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("hotelbooking").actions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders?.length > 0 ? (
                    orders?.map((order) => {
                      return (
                        <tr className="border-t border-[#E4E4E4] ">
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.bookingId}
                          </td>
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.hotelId?.name}
                          </td>
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.userId?.name}
                          </td>
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.rooms}
                          </td>

                          <td className="py-[20px] px-[16px] ">
                            {order?.checkInDate &&
                              new Date(order?.checkInDate).toLocaleDateString(
                                "en-GB"
                              )}
                          </td>
                          <td className="py-[20px] px-[16px] text-base text-black  whitespace-nowrap">
                            {order?.checkOutDate &&
                              new Date(order?.checkOutDate).toLocaleDateString(
                                "en-GB"
                              )}
                          </td>
                          <td className="py-[20px] px-[16px] ">
                            {order?.guests}
                          </td>
                          <td className="py-[20px] px-[16px]">
                            {order?.activities?.length > 0
                              ? order?.activities
                                ?.map((activity) => activity.name)
                                .join(", ")
                              : "-"}
                          </td>

                          <td className="py-[20px] px-[16px]">
                            <div
                              className={`px-[20px] py-2 w-full font-normal rounded-full text-center text-sm 
      ${order?.bookingStatus?.toLowerCase() === "pending" ||
                                  order?.bookingStatus?.toLowerCase() === "awaiting payment"
                                  ? "bg-yellow-500 text-white"
                                  : order?.bookingStatus?.toLowerCase() === "booked"
                                    ? "bg-[#4CAF50] text-white"
                                    : order?.bookingStatus?.toLowerCase() === "cancelled"
                                      ? "bg-red-500 text-white"
                                      : "bg-gray-300 text-black"
                                }
    `}
                            >
                              {order?.bookingStatus}
                            </div>
                          </td>
                          <td className="py-[20px] px-[16px] ">
                            {order?.paymentStatus}
                          </td>

                          <td className="py-[20px] px-[16px]  flex gap-[12px]">
                            <a
                              href="#"
                              onClick={() =>
                                openModel(order?._id, order?.bookingStatus)
                              }
                              className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                            >
                              <IoMdEye className="text-base text-black" />
                            </a>

                            <button
                              onClick={() => handleDelete(order?._id)}
                              className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                            >
                              <RiDeleteBin6Line className="text-base text-black" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={11}>
                        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                          <div className="w-16 h-16 rounded-full bg-[#EEF7EF] flex items-center justify-center text-[#4CAF50] mb-3">
                            <RiHotelLine size={28} />
                          </div>
                          <p className="text-xl font-semibold text-[#18181B]">
                            {t("hotelbooking").noReservations || "No reservations found."}
                          </p>
                          <p className="text-sm text-[#6B7280]">
                            {t("hotelbooking").noReservationsHint ||
                              "Try adjusting filters or search with another keyword."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {orders?.length > 0 && (
            <Pagination
              page={pagination.currentPage}
              totalPages={pagination.totalPages}
              setPage={setPage}
            />
          )}
        </div>
      </div>

      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div className="w-full rounded-[20px] md:p-[30px] py-[20px] px-[15px]">
          <div className="grid grid-col-1 sm:grid-cols-3 justify-between text-left pb-[21px] border-b border-[#EEEEEE] mb-[18px]">
            <h2 className="text-[22px] font-medium col-span-2">
              {t("hotelbooking").bookingDetail}
            </h2>
          </div>
          <div className="h-[350px] overflow-y-auto">
            <p className="text-[17px] font-medium mb-[15px]">
              {t("hotelbooking").bookingId}: {orderDetail?.bookingId} -{" "}
              {orderDetail?.userId?.name}
              's Booking
            </p>
            <div className="border border-[#DDDDDD] rounded-[5px] xl:gap-[47px] gap-[20px] p-[20px] pb-[36px] bg-white grid lg:grid-cols-[auto_auto_1fr] md:grid-cols-2 grid-cols-1">
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("hotelbooking").bookingInformation}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("hotelbooking").bookingId}:
                    </span>
                    {orderDetail?.bookingId}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("hotelbooking").name}:
                    </span>
                    {orderDetail?.userId?.name}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("hotelbooking").totalGuests}:
                    </span>
                    {orderDetail?.guests}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("hotelbooking").checkInDate}:
                    </span>{" "}
                    {orderDetail?.checkInDate &&
                      new Date(orderDetail?.checkInDate).toLocaleDateString(
                        "en-GB"
                      )}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("hotelbooking").checkOutDate}:
                    </span>
                    {orderDetail?.checkOutDate &&
                      new Date(orderDetail?.checkOutDate).toLocaleDateString(
                        "en-GB"
                      )}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("hotelbooking").roomNumber}:
                    </span>
                    {orderDetail?.rooms}
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("hotelbooking").paymentInformation}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("hotelbooking").paymentMethod}:
                    </span>
                    {orderDetail?.paymentMethod}
                  </li>
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("hotelbooking").paymentStatus}:
                    </span>{" "}
                    {orderDetail?.paymentStatus}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("hotelbooking").subTotal}:
                    </span>{" "}
                    ₾{orderDetail?.subtotal}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("hotelbooking").discount}:
                    </span>{" "}
                    ₾{orderDetail?.discountAmount}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">Total :</span> ₾
                    {orderDetail?.totalAmount}
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("hotelbooking").bookingStatus}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("hotelbooking").currentStatus}:
                    </span>{" "}
                    {orderDetail?.bookingStatus}
                  </li>
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("hotelbooking").notes}:
                    </span>{" "}
                    {orderDetail?.notes}
                  </li>
                </ul>
              </div>
            </div>

            {orderDetail?.bookingStatus !== "pending" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-[20px] mt-5">
                  <div className="max-w-[473px]">
                    <h2 className="text-[17px] font-medium mb-[10px]">
                      {t("hotelbooking").updateBookingStatus}
                    </h2>
                    <p className="text-[15px] text-[#605D5D] font-normal">
                      {t("hotelbooking").updateBookingStatusDescription}
                    </p>
                  </div>
                  <div className="w-full sm:max-w-[324px] sm:ml-auto">
                    <h2 className="text-[17px] font-medium mb-[10px]">
                      {t("hotelbooking").bookingStatus}
                    </h2>
                    <div className="flex items-center sm:flex-row flex-col gap-[10px]">
                      <div className="flex border border-[#DDDDDD] rounded-md overflow-hidden w-full sm:max-w-[184px]">
                        <select
                          name="bookingStatus"
                          value={formData.bookingStatus}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              bookingStatus: e.target.value,
                            }))
                          }
                          className={`border border-[#DDDDDD]
                         rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50]`}
                        >
                          <option value="available">
                            {t("hotelbooking").statusOptions.FiEditavailable}
                          </option>
                          <option value="checked-in">
                            {t("hotelbooking").statusOptions.checkedIn}
                          </option>
                          <option value="checked-out">
                            {t("hotelbooking").statusOptions.checkedOut}
                          </option>
                          <option value="completed">
                            {t("hotelbooking").statusOptions.completed}
                          </option>
                          <option value="cancelled">
                            {t("hotelbooking").statusOptions.cancelled}
                          </option>
                          <option value="booked">
                            {t("hotelbooking").statusOptions.booked}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-[20px] mt-5">
                  <div className="max-w-[473px]">
                    <h2 className="text-[17px] font-medium mb-[10px]">
                      {t("hotelbooking").updatePaymentStatus}
                    </h2>
                    <p className="text-[15px] text-[#605D5D] font-normal">
                      {t("hotelbooking").updatePaymentStatusDescription}
                    </p>
                  </div>
                  <div className="w-full sm:max-w-[324px] sm:ml-auto">
                    <h2 className="text-[17px] font-medium mb-[10px]">
                      {t("hotelbooking").paymentStatus}
                    </h2>
                    <div className="flex items-center sm:flex-row flex-col gap-[10px]">
                      <div className="flex border border-[#DDDDDD] rounded-md overflow-hidden w-full sm:max-w-[184px]">
                        <select
                          name="paymentStatus"
                          value={formData.paymentStatus}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              paymentStatus: e.target.value,
                            }))
                          }
                          className={`border border-[#DDDDDD]
                         rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50]`}
                        >
                          <option value="pending">
                            {t("hotelbooking").paymentOptions.pending}
                          </option>
                          <option value="paid">
                            {t("hotelbooking").paymentOptions.paid}
                          </option>
                          <option value="failed">
                            {t("hotelbooking").paymentOptions.failed}
                          </option>
                          <option value="refunded">
                            {t("hotelbooking").paymentOptions.refunded}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {orderDetail?.bookingStatus !== "pending" && (
              <div className="flex sm:flex-row flex-col md:gap-[30px] gap-[10px] justify-end pt-[23px] mt-[30px] border-t border-[#EEEEEE]">
                <button
                  onClick={handleUpdateStatus}
                  className="relative right-4 inline-block sm:w-max w-full rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold py-[14px] px-[27px] green-shadow hover:bg-[#209b25] mt-4"
                >
                  {t("hotelbooking").updateBookingStatus}
                </button>
              </div>
            )}
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default ForRestaurants;
